import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Tabs, TabsContent, TabsList, TabsTrigger } from "@nexus/ui";
import { ArrowLeft, Plus } from "lucide-react";
import type { AdminVendorRow } from "@/features/admin/marketplace/types/admin-marketplace.types";
import { usePermissions } from "@/features/auth/hooks/usePermissions";
import { AdminVendorsList } from "../components/AdminVendorsList";
import { AdminVendorForm } from "../components/AdminVendorForm";
import { AdminTeamRecipients } from "../components/AdminTeamRecipients";

type PageMode =
  | { kind: "list" }
  | { kind: "form"; editingVendor?: AdminVendorRow };

type AdminTab = "vendors" | "team-recipients";

export function MarketplaceAdminPage() {
  const { t } = useTranslation();
  const perms = usePermissions();
  const [mode, setMode] = useState<PageMode>({ kind: "list" });
  const [tab, setTab] = useState<AdminTab>("vendors");

  if (mode.kind === "form") {
    return (
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 p-4 sm:p-6">
        <header className="flex flex-col gap-3">
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 h-7 w-fit px-2 text-muted-foreground hover:text-foreground"
            onClick={() => setMode({ kind: "list" })}
          >
            <ArrowLeft className="mr-1.5 h-4 w-4" />
            {t("marketplace.admin.backToVendors", { defaultValue: "All vendors" })}
          </Button>
          <div className="flex flex-col gap-1">
            <h1 className="text-2xl font-semibold tracking-tight">
              {mode.editingVendor
                ? t("marketplace.admin.editVendor")
                : t("marketplace.admin.addVendor")}
            </h1>
            <p className="text-sm text-muted-foreground">
              {t("marketplace.admin.formSubtitle")}
            </p>
          </div>
        </header>

        <AdminVendorForm
          key={mode.editingVendor?.id ?? "new"}
          vendor={mode.editingVendor}
          onClose={() => setMode({ kind: "list" })}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 p-4 sm:p-6">
      <header className="flex items-center justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("marketplace.admin.title")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("marketplace.admin.subtitle")}
          </p>
        </div>
        {tab === "vendors" && perms.MARKETPLACE_ADMIN_CREATE_VENDOR.isOn && (
          <Button onClick={() => setMode({ kind: "form" })}>
            <Plus className="mr-2 h-4 w-4" />
            {t("marketplace.admin.addVendor")}
          </Button>
        )}
      </header>

      <Tabs value={tab} onValueChange={(value) => setTab(value as AdminTab)}>
        <TabsList variant="line" className="w-full justify-start">
          <TabsTrigger value="vendors">
            {t("marketplace.admin.tabs.vendors")}
          </TabsTrigger>
          <TabsTrigger value="team-recipients">
            {t("marketplace.admin.tabs.teamRecipients")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="vendors" className="mt-6">
          <AdminVendorsList
            canList={perms.MARKETPLACE_ADMIN_LIST_VENDORS.isOn}
            canEdit={perms.MARKETPLACE_ADMIN_REPLACE_VENDOR.isOn}
            canActivate={perms.MARKETPLACE_ADMIN_ACTIVATE_VENDOR.isOn}
            canDeactivate={perms.MARKETPLACE_ADMIN_DEACTIVATE_VENDOR.isOn}
            onEdit={(vendor) => setMode({ kind: "form", editingVendor: vendor })}
          />
        </TabsContent>

        <TabsContent value="team-recipients" className="mt-6">
          <AdminTeamRecipients
            canList={perms.MARKETPLACE_ADMIN_LIST_TEAM_RECIPIENTS.isOn}
            canCreate={perms.MARKETPLACE_ADMIN_CREATE_TEAM_RECIPIENT.isOn}
            canUpdate={perms.MARKETPLACE_ADMIN_UPDATE_TEAM_RECIPIENT.isOn}
            canDelete={perms.MARKETPLACE_ADMIN_DELETE_TEAM_RECIPIENT.isOn}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
